import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQS = [
  {
    question: 'What is the best Zotero file organization tool?',
    answer:
      "Sortio renames PDFs by content (author, year, title), routes them into project folders, and is shipping a native Zotero integration in the next release. Once live, Sortio will upload each file directly into the matching Zotero collection as an item with attachment, with Crossref-enriched metadata and a generated citation key. Until then, use Sortio as a pre-step that renames downloaded PDFs cleanly before you drag them into Zotero.",
  },
  {
    question: 'How does Sortio rename academic PDFs by author and year?',
    answer:
      "Sortio reads each PDF for the title page, first author, year, journal, and DOI. ssrn-id3942817.pdf becomes Vaswani_2017_Attention-is-all-you-need.pdf. The exact pattern is configurable: Author_Year_TitleShort.pdf, YEAR - AUTHOR - TITLE.pdf, or your lab's own convention via the Rule Builder. For sparse-metadata old OCR'd scans, Sortio enriches via Crossref so the rename still works.",
  },
  {
    question: 'Will my unpublished manuscripts leave my machine?',
    answer:
      "Not unless you choose the cloud classifier. In Ollama mode no document content (and no filenames) crosses the network. In BYOK mode requests go from your machine directly to your provider on your account, never through Sortio. With the default Sortio classifier, file content stays local and only filenames plus extracted metadata are sent for matching, redacted from logs after 30 days. For unpublished or IRB-sensitive work, use Ollama mode.",
  },
  {
    question: 'Does Sortio work with Mendeley or EndNote, not just Zotero?',
    answer:
      "Yes. Sortio is filesystem-transparent: it organizes the underlying PDFs and writes clean filenames, which Mendeley and EndNote will then re-parse correctly when you add them to their libraries. Native API integration is Zotero-first because Zotero has the most open API and the largest researcher base, but Mendeley and EndNote users get the rename-and-route benefit through the standard Sortio workflow.",
  },
  {
    question: 'How does Sortio handle preprints versus camera-ready versions?',
    answer:
      "Sortio recognizes the same paper as preprint (arXiv, SSRN, bioRxiv) and as camera-ready (NeurIPS, Nature, ACL) by matching titles and authors. The camera-ready version is treated as canonical and the preprint is kept alongside in a preprints subfolder, both renamed consistently. The Rule Builder lets you decide which version your bibliography prefers and routes citations accordingly.",
  },
  {
    question: 'Can Sortio organize my supplemental data and notebooks alongside the paper?',
    answer:
      "Yes. CSVs, parquet files, Jupyter notebook exports, raw experimental logs are routed under the matching paper folder. The pattern is Paper / data / and Paper / notebooks / so future you (or a reviewer six months from now) can find the exact dataset that produced a figure without crawling a separate scratch directory.",
  },
  {
    question: 'How does Sortio help with version control on draft chapters?',
    answer:
      "Sortio tags each Word, LaTeX, or text document with the modification date, the chapter or section name extracted from the file, and the reviewer or coauthor name if it appears in the filename. thesis_v3_FINAL_REAL.docx becomes 2026-04-12 - Thesis Chapter 4 - after Advisor.docx with earlier versions moved to a superseded subfolder. Sortio does not replace Git for the actual diff, but it makes the version timeline visible.",
  },
  {
    question: 'Does Sortio export citations as BibTeX, RIS, or CSL JSON?',
    answer:
      "Sortio does not currently generate BibTeX directly; it organizes files and metadata. Zotero is your citation generator. When the Zotero integration ships, each file Sortio uploads triggers Zotero's metadata extraction and citation key generation, which you can then export as BibTeX, RIS, or CSL JSON from Zotero in the usual way.",
  },
  {
    question: 'Can a lab PI standardize file naming across the lab?',
    answer:
      "Yes. Create a rule on one machine, export it as a JSON file, and import it on each lab member's install. Team plan ($29 per seat per month) adds a shared rule library so updates propagate automatically. The lab keeps a single naming convention even as new students join.",
  },
  {
    question: 'How fast is it on a 5,000 paper Zotero library backlog?',
    answer:
      "For a one-time historical sort, use the Rule Builder. Rule-based sorts process unlimited files instantly and never touch your AI allowance. We typically see a 5,000-paper library renamed and routed in under an hour on a modern Mac. New papers landing afterward in Downloads can use AI Sort (which is more flexible and draws from your AI allowance) or be funneled through the same rule.",
  },
  {
    question: 'Does it work on Linux for HPC and research compute environments?',
    answer:
      "Yes. Sortio ships for macOS, Windows, and Linux. Researchers on Linux compute clusters can run Sortio against shared scratch space or home directories. The Free tier is sufficient to test the workflow on a single project folder before committing.",
  },
  {
    question: 'What does Sortio cost for a graduate student?',
    answer:
      "Free tier: a one-time AI trial allowance, enough to try one project folder. Pro: $14.99 per month or $99 per year (about $8 per month annualized), includes a monthly AI allowance sized for heavy use and the Zotero integration. Many PhD students run a one-time Pro month to clean their historical library, then downgrade once the backlog is done.",
  },
];

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="mb-4 transition-all duration-300 transform hover:translate-x-1">
    <div className={`apple-panel transition-all duration-300 ${isOpen ? 'ring-1 ring-sortio-primary/30' : ''}`}>
      <button className="flex justify-between items-center w-full p-5 text-left rounded-apple focus:outline-none" onClick={onToggle} aria-expanded={isOpen}>
        <span className="text-base font-medium text-apple-50 pr-4">{question}</span>
        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-300 ${isOpen ? 'bg-sortio-primary/20' : 'bg-apple-600/40'}`}>
          {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-sortio-primary" /> : <ChevronDown className="w-3.5 h-3.5 text-apple-300" />}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-5 pt-0">
          <p className="text-apple-300 text-sm leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  </div>
);

const ResearchersFAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  return (
    <section id="researchers-faq" className="my-20">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="flex flex-col items-center mb-12">
        <div className="mb-4 rounded-full bg-apple-700/60 backdrop-blur-sm p-2 border border-apple-600/20">
          <HelpCircle className="w-6 h-6 text-sortio-primary" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50 text-center">
          Researcher <span className="apple-gradient-text">FAQ</span>
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl text-center">
          Questions from PhD students, postdocs, PIs, and research staff evaluating Sortio for
          PDF organization and Zotero workflow.
        </p>
      </div>

      <div className="max-w-3xl mx-auto">
        {FAQS.map((faq, index) => (
          <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? -1 : index)} />
        ))}
      </div>
    </section>
  );
};

export default ResearchersFAQ;
